import * as React from 'react'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useTable } from '@/modules/drivers/context/TableContext'
import { useQueryDrivers } from '@/lib/api/company/drivers/useQueryDrivers'
import type {
  Driver,
  DriversResponse,
  PaginatedDriversResponse,
} from '@/lib/api/company/drivers/types'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import BillingDriverRow from './BillingDriverRow'
import { getBillingDriversColumns } from '../constants/tableColumns'
import {
  useQueryCompanyBillingDrivers,
  useQueryCompanyBillingUsers,
} from '../api/useQueryBillingStats'
import type { BillingPeriod, DriverBillingStats } from '../types'

interface BillingDriversTableProps {
  companyId: string
  period: BillingPeriod
  search?: string
}

const unwrapDrivers = (resp: DriversResponse | PaginatedDriversResponse | undefined): Driver[] => {
  if (!resp) return []
  return Array.isArray(resp) ? resp : resp.drivers
}

const BillingDriversTable: React.FC<BillingDriversTableProps> = ({
  companyId,
  period,
  search = '',
}) => {
  const { t } = useLocalize()
  const columns = React.useMemo(() => getBillingDriversColumns(t), [t])
  const loading = useLoadingStore(selectLoading)

  const driversQuery = useQueryDrivers({ companyId: String(companyId) })
  const drivers = React.useMemo(() => unwrapDrivers(driversQuery.data), [driversQuery.data])

  const { data: driverStats = [], isLoading: isStatsLoading } =
    useQueryCompanyBillingDrivers(companyId, period)
  // Kept so the action-bar pill row (rendered by a sibling) shares the same
  // cache and avoids a duplicate request; the per-row display name now comes
  // resolved from /billing/drivers so this hook is no longer load-bearing here.
  useQueryCompanyBillingUsers(companyId, period)

  const {
    toggleAll,
    toggleId,
    selectedGroup,
    setAllIds,
    isSelectedAny,
  } = useTable()

  const statsByDriver = React.useMemo(() => {
    const m = new Map<number, DriverBillingStats>()
    for (const s of driverStats) m.set(s.driverId, s)
    return m
  }, [driverStats])

  const filtered = React.useMemo(() => {
    if (!search) return drivers
    const s = search.toLowerCase()
    return drivers.filter(
      (d) =>
        d.fullName?.toLowerCase().includes(s) ||
        d.surname?.toLowerCase().includes(s) ||
        d.name?.toLowerCase().includes(s),
    )
  }, [drivers, search])

  React.useEffect(() => {
    setAllIds(filtered.map((d) => ({ id: d.id, isActive: d.isActive })))
  }, [filtered, setAllIds])

  // Project sortable fields onto each row so numeric/date columns sort correctly.
  const sortable = React.useMemo(() => {
    return filtered.map((d) => {
      const s = statsByDriver.get(d.id)
      return {
        ...d,
        wasActive: s?.wasActive ?? d.isActive,
        timesheetCalculationsCount: s?.timesheetCalculationsCount ?? 0,
        timesheetRecalculationsCount: s?.timesheetRecalculationsCount ?? 0,
        importedActivityDays: s?.importedActivityDays ?? 0,
        printoutsGeneratedCount: s?.printoutsGeneratedCount ?? 0,
        exportsGeneratedCount: s?.exportsGeneratedCount ?? 0,
        lastEventAt: s?.lastEventAt ?? null,
        lastEventByUserDisplay:
          s?.lastEventByUserName ?? s?.lastEventByUserEmail ?? s?.lastEventByUserId ?? '',
      } as Driver & Record<string, unknown>
    })
  }, [filtered, statsByDriver])

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: sortable,
    tableColumns: columns,
  })

  const isLoadingData = loading || driversQuery.isLoading || isStatsLoading

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {isLoadingData ? (
        <div className="tw-grid tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : sortedData.length > 0 ? (
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              toggleAll={toggleAll}
              isSelectedAny={isSelectedAny}
              columns={columns}
              sortConfig={sortConfig}
              sticky
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((d) => (
                <BillingDriverRow
                  key={d.id}
                  driver={d as Driver}
                  stats={statsByDriver.get((d as Driver).id)}
                  companyId={companyId}
                  period={period}
                  selectedIds={selectedGroup}
                  toggleId={toggleId}
                  isSelectedAny={isSelectedAny}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <TableHeader
            opacity
            toggleAll={toggleAll}
            isSelectedAny={isSelectedAny}
            columns={columns}
            sortConfig={sortConfig}
            onHeaderClick={handleHeaderClick}
          />
          <TableEmpty title={t('billing.empty')} />
        </>
      )}
    </section>
  )
}

export default BillingDriversTable
